import * as React from "react";
import { createRoot } from "react-dom/client";
import { AlertTriangle, CheckCircle2, ChevronDown, Info, X, XCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  dismissToast,
  initialsOf,
  registerToasterMount,
  subscribeToasts,
  getToasts,
  type ToastRecord,
  type ToastTone,
} from "./toast";

/* ----------------------------------------------------------------- Avatar */

const avatarSizes = {
  xs: "size-6 text-xs",
  sm: "size-8 text-xs",
  md: "size-10 text-sm",
  lg: "size-12 text-base",
  xl: "size-16 text-lg",
};

export interface AvatarProps {
  src?: string | null;
  /** Full name; used for the alt text and the initials fallback */
  name?: string | null;
  size?: keyof typeof avatarSizes;
  className?: string;
}

/** Round photo with a neutral initials fallback that also survives broken image URLs */
export const Avatar: React.FC<AvatarProps> = ({ src, name, size = "md", className }) => {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [src]);
  const showImg = Boolean(src) && !failed;
  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 font-semibold text-gray-600 ring-1 ring-gray-200",
        avatarSizes[size],
        className
      )}
    >
      {showImg ? (
        <img src={src!} alt={name ?? ""} className="size-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span aria-label={name ?? undefined} role={name ? "img" : undefined}>
          {initialsOf(name)}
        </span>
      )}
    </span>
  );
};

/* ------------------------------------------------------------- OptionTile */

export interface OptionTileProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "title"> {
  type?: "radio" | "checkbox";
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
}

/** Selectable card wrapping a radio or checkbox (seller type, deadlines, preferences) */
export const OptionTile = React.forwardRef<HTMLInputElement, OptionTileProps>(
  ({ type = "radio", title, description, icon, className, disabled, ...props }, ref) => (
    <label
      className={cn(
        "group relative flex cursor-pointer items-start gap-3 rounded-xl bg-white p-4 ring-1 ring-inset ring-gray-200 transition-[box-shadow,background-color]",
        "hover:ring-gray-300 has-[:checked]:bg-brand-50/50 has-[:checked]:ring-2 has-[:checked]:ring-brand-500",
        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500",
        disabled && "cursor-not-allowed opacity-60",
        className
      )}
    >
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        className={cn("mt-0.5 size-4 shrink-0 cursor-pointer accent-brand-600", type === "checkbox" && "rounded")}
        {...props}
      />
      {icon && (
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 group-has-[:checked]:bg-brand-100 group-has-[:checked]:text-brand-700 [&_svg]:size-4">
          {icon}
        </span>
      )}
      <span className="min-w-0">
        <span className="block text-sm font-medium text-gray-900">{title}</span>
        {description && <span className="mt-0.5 block text-meta text-gray-500">{description}</span>}
      </span>
    </label>
  )
);
OptionTile.displayName = "OptionTile";

/* -------------------------------------------------------------- Accordion */

export interface AccordionItem {
  id?: string;
  title: React.ReactNode;
  content: React.ReactNode;
}

export interface AccordionProps {
  items: AccordionItem[];
  /** Allow several panels open at once */
  multiple?: boolean;
  defaultOpen?: number[];
  className?: string;
}

/** Disclosure list (FAQs, help). Buttons carry aria-expanded / aria-controls. */
export const Accordion: React.FC<AccordionProps> = ({ items, multiple, defaultOpen = [], className }) => {
  const [open, setOpen] = React.useState<number[]>(defaultOpen);
  const baseId = React.useId();
  const toggle = (i: number) =>
    setOpen((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : multiple ? [...prev, i] : [i]
    );
  return (
    <div className={cn("divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white", className)}>
      {items.map((item, i) => {
        const isOpen = open.includes(i);
        const panelId = `${baseId}-panel-${i}`;
        const buttonId = `${baseId}-button-${i}`;
        return (
          <div key={item.id ?? i}>
            <h3>
              <button
                id={buttonId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(i)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50/60 sm:px-6"
              >
                {item.title}
                <ChevronDown
                  className={cn("size-4 shrink-0 text-gray-400 transition-transform duration-200", isOpen && "rotate-180")}
                />
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              hidden={!isOpen}
              className="px-5 pb-5 text-sm leading-relaxed text-gray-600 sm:px-6"
            >
              {item.content}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ------------------------------------------------------------------ Toast */

registerToasterMount(() => {
  const host = document.createElement("div");
  host.setAttribute("data-toaster", "");
  document.body.appendChild(host);
  createRoot(host).render(<Toaster />);
});

const toastIcons: Record<ToastTone, React.ReactNode> = {
  success: <CheckCircle2 className="size-5 text-emerald-500" />,
  error: <XCircle className="size-5 text-red-500" />,
  info: <Info className="size-5 text-sky-500" />,
  warning: <AlertTriangle className="size-5 text-amber-500" />,
};

const ToastItem: React.FC<{ t: ToastRecord }> = ({ t }) => {
  React.useEffect(() => {
    if (!t.duration) return;
    const timer = window.setTimeout(() => dismissToast(t.id), t.duration);
    return () => window.clearTimeout(timer);
  }, [t.id, t.duration]);
  return (
    <div
      role={t.tone === "error" ? "alert" : "status"}
      className="pointer-events-auto flex w-full animate-toast-in items-start gap-3 rounded-xl bg-white p-4 shadow-overlay ring-1 ring-gray-950/5"
    >
      <span className="shrink-0">{toastIcons[t.tone]}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">{t.title}</p>
        {t.description && <p className="mt-0.5 text-meta text-gray-500">{t.description}</p>}
      </div>
      <button
        type="button"
        onClick={() => dismissToast(t.id)}
        aria-label="Dismiss notification"
        className="-m-1 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
      >
        <X className="size-4" />
      </button>
    </div>
  );
};

/** Renders active toasts. Mounted automatically by `toast()`; render manually only in tests. */
export const Toaster: React.FC = () => {
  const items = React.useSyncExternalStore(subscribeToasts, getToasts, getToasts);
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[1200] flex flex-col items-center gap-2 p-4 sm:bottom-4 sm:left-auto sm:right-4 sm:w-96 sm:items-end sm:p-0"
    >
      {items.map((t) => (
        <ToastItem key={t.id} t={t} />
      ))}
    </div>
  );
};
